
import React, { useEffect, useState } from "react";
import { useAuth } from "@/components/auth/AuthProvider";
import { Navigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import Layout from "@/components/layout/Layout";
import { supabase } from "@/integrations/supabase/client";
import { OwnerDashboardHeader } from "@/components/nursing-homes/owner/OwnerDashboardHeader";
import { ClaimedNursingHomes } from "@/components/nursing-homes/owner/ClaimedNursingHomes";
import { PendingClaims } from "@/components/nursing-homes/owner/PendingClaims";
import { toast } from "sonner";

export default function OwnerDashboardPage() {
  const { user, isLoading: authLoading } = useAuth();
  const [claimedHomes, setClaimedHomes] = useState<any[]>([]);
  const [pendingClaims, setPendingClaims] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchOwnerData = async () => {
      if (!user) return;
      
      try {
        setIsLoading(true);
        console.log("Fetching nursing home claims for user:", user.id);
        
        // Get ALL claims for this user (both pending and approved)
        const { data: allClaims, error: claimsError } = await supabase
          .from('nursing_home_claims')
          .select(`
            id,
            nursing_home_id,
            status,
            created_at,
            updated_at,
            notes
          `)
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });
        
        if (claimsError) throw claimsError;
        console.log("Fetched all claims:", allClaims);
        
        if (allClaims && allClaims.length > 0) {
          // Separate pending and approved claims
          const approvedClaims = allClaims.filter(claim => claim.status === 'approved');
          const pendingClaimsData = allClaims.filter(claim => claim.status !== 'approved');
          
          // Get nursing home details for all claims
          const allNursingHomeIds = allClaims.map(claim => claim.nursing_home_id);
          console.log("Fetching nursing homes with IDs:", allNursingHomeIds);
          
          const { data: homes, error: homesError } = await supabase
            .from('nursing_homes')
            .select('*')
            .in('nh_slug', allNursingHomeIds);
          
          if (homesError) throw homesError;
          console.log("Fetched nursing homes:", homes);
          
          // Set approved homes
          const approvedHomes = approvedClaims
            .map(claim => homes?.find(home => home.nh_slug === claim.nursing_home_id))
            .filter(Boolean);
          setClaimedHomes(approvedHomes || []);
          
          // Set pending claims with nursing home details
          const pendingClaimsWithHomes = pendingClaimsData.map(claim => ({
            ...claim,
            nursing_home: homes?.find(home => home.nh_slug === claim.nursing_home_id)
          }));
          setPendingClaims(pendingClaimsWithHomes);
          
        } else {
          console.log("No claims found");
          setClaimedHomes([]);
          setPendingClaims([]);
        }
      } catch (err) {
        console.error("Error fetching owner data:", err);
        setError(err instanceof Error ? err : new Error('Unknown error'));
        toast.error("Failed to load your nursing homes");
      } finally {
        setIsLoading(false);
      }
    };

    if (user && !authLoading) {
      fetchOwnerData();
    }
  }, [user, authLoading]);

  // Redirect to login if not authenticated
  if (!authLoading && !user) {
    return <Navigate to="/auth/login?redirectTo=/owner/dashboard" />;
  }

  return (
    <Layout>
      <Helmet>
        <title>Owner Dashboard | Grandpal</title>
      </Helmet>

      <div className="container py-4 md:py-8 max-w-7xl">
        <OwnerDashboardHeader user={user} />
        
        <div className="grid grid-cols-1 gap-4 md:gap-6 mt-4">
          <PendingClaims 
            claims={pendingClaims}
            isLoading={isLoading}
          />
          <ClaimedNursingHomes 
            homes={claimedHomes} 
            isLoading={isLoading}
            error={error}
          />
        </div>
      </div>
    </Layout>
  );
}
